
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { ScrollArea } from "@/components/ui/scroll-area";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Loader2 } from "lucide-react";
import { PageVisitStats } from "@/types/analytics";
import {
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
  Legend,
  Tooltip,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
} from "recharts";
import ErrorDisplay from "@/components/ErrorDisplay";

// Colors for charts
const COLORS = ['#3B82F6', '#10B981', '#F97316', '#A855F7', '#EC4899', '#06B6D4', '#F59E0B'];

export default function PageAnalytics() {
  // Fetch page visit analytics
  const { data: pageStats, isLoading, error } = useQuery({
    queryKey: ['pageAnalytics'],
    queryFn: async () => {
      // Use direct query since get_page_analytics doesn't exist yet
      const { data: analyticsData, error: fetchError } = await supabase
        .from('user_analytics')
        .select('*')
        .eq('action_type', 'page_view');
      
      if (fetchError) throw fetchError;
      
      // Process the raw data to get page statistics
      const pageMap: Record<string, { visits: number, total_time: number }> = {};
      
      analyticsData?.forEach(item => {
        if (!pageMap[item.page_path]) {
          pageMap[item.page_path] = { visits: 0, total_time: 0 };
        }
        
        pageMap[item.page_path].visits += 1;
        pageMap[item.page_path].total_time += item.time_spent || 0;
      });
      
      return Object.entries(pageMap).map(([page_path, stats]) => ({
        page_path,
        visit_count: stats.visits,
        total_time: stats.total_time,
        average_time: stats.visits > 0 ? stats.total_time / stats.visits : 0
      })) as PageVisitStats[];
    },
  });
  
  if (error) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>Page Analytics</CardTitle>
        </CardHeader>
        <CardContent>
          <ErrorDisplay error={error as Error} />
        </CardContent>
      </Card>
    );
  }
  
  // Prepare data for pie chart
  const pieChartData = pageStats?.map((page, index) => ({
    name: page.page_path,
    value: page.visit_count,
    color: COLORS[index % COLORS.length]
  }));
  
  // Prepare data for bar chart
  const barChartData = pageStats?.map((page, index) => ({
    name: page.page_path,
    avgTime: Math.round(page.average_time / 60 * 10) / 10, // Convert to minutes with 1 decimal
    color: COLORS[index % COLORS.length]
  }));

  return (
    <Card className="h-full">
      <CardHeader>
        <CardTitle>Page Analytics</CardTitle>
        <CardDescription>
          Page visits and time spent by users
        </CardDescription>
      </CardHeader>
      <CardContent>
        {isLoading ? (
          <div className="flex justify-center py-8">
            <Loader2 className="h-8 w-8 animate-spin" />
          </div>
        ) : pageStats && pageStats.length > 0 ? (
          <div className="space-y-8">
            <div>
              <h3 className="text-lg font-medium mb-4">Page Visits Distribution</h3>
              <ResponsiveContainer width="100%" height={300}>
                <PieChart>
                  <Pie
                    data={pieChartData}
                    dataKey="value"
                    nameKey="name"
                    cx="50%"
                    cy="50%"
                    outerRadius={100}
                    label={({ name }) => name}
                  >
                    {pieChartData?.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={entry.color} />
                    ))}
                  </Pie>
                  <Tooltip 
                    formatter={(value) => [`${value} visits`, 'Visits']}
                  />
                  <Legend />
                </PieChart>
              </ResponsiveContainer>
            </div>
            
            <div>
              <h3 className="text-lg font-medium mb-4">Average Time Spent (minutes)</h3>
              <ResponsiveContainer width="100%" height={300}>
                <BarChart data={barChartData}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="name" />
                  <YAxis />
                  <Tooltip 
                    formatter={(value) => [`${value} minutes`, 'Avg. Time']}
                  />
                  <Bar dataKey="avgTime">
                    {barChartData?.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={entry.color} />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </div>
            
            <div>
              <h3 className="text-lg font-medium mb-4">Detailed Page Statistics</h3>
              <ScrollArea className="h-[200px]">
                <table className="w-full border-collapse">
                  <thead>
                    <tr className="border-b">
                      <th className="text-left p-2">Page</th>
                      <th className="text-right p-2">Visits</th>
                      <th className="text-right p-2">Avg. Time</th>
                      <th className="text-right p-2">Total Time</th>
                    </tr>
                  </thead>
                  <tbody>
                    {pageStats.map((page) => (
                      <tr key={page.page_path} className="border-b">
                        <td className="p-2">{page.page_path}</td>
                        <td className="text-right p-2">{page.visit_count}</td>
                        <td className="text-right p-2">
                          {Math.round(page.average_time / 60 * 10) / 10} min
                        </td>
                        <td className="text-right p-2">
                          {Math.round(page.total_time / 60)} min
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </ScrollArea>
            </div>
          </div>
        ) : (
          <div className="py-8 text-center text-muted-foreground">
            No page analytics data available yet
          </div>
        )}
      </CardContent>
    </Card>
  );
}
